"use strict";

const FORM_SELECTOR = '[data-product-filters]';
const RESULTS_SELECTOR = '[data-products-results]';
const MOSAIC_SELECTOR = '[data-products-mosaic]';
const TOGGLE_SELECTOR = '[data-product-filters-toggle]';
const RESET_SELECTOR = '[data-product-filters-reset]';
const PANEL_SELECTOR = '[data-product-filters-panel]';
const AJAX_ACTION = 'product_filters_action';
const FILTER_KEYS = ['categories', 'attributes', 'collections', 'designers'];
const MOBILE_MQ = '(max-width: 991.98px)';
const BODY_OFFCANVAS_CLASS = 'product-filters-offcanvas-open';

function hasActiveFilters(form) {
    return form.querySelectorAll('input[type="checkbox"]:checked').length > 0;
}

function setCount(el, count) {
    if (!el) {
        return;
    }

    el.textContent = String(count);

    if (count > 0) {
        el.removeAttribute('hidden');
        return;
    }

    el.setAttribute('hidden', '');
}

function syncFilterCounts(form) {
    const totalCountEl = form.querySelector('[data-product-filters-total-count]');
    let total = 0;

    form.querySelectorAll('[data-product-filters-group]').forEach(function (group) {
        const count = group.querySelectorAll('input[type="checkbox"]:checked').length;
        const countEl = group.querySelector('[data-product-filters-group-count]');

        setCount(countEl, count);
        total += count;
    });

    setCount(totalCountEl, total);
}

function getFormFilters(form) {
    const filters = {};

    FILTER_KEYS.forEach(function (key) {
        const values = Array.from(form.querySelectorAll(`input[name="${key}[]"]:checked`))
            .map(function (input) {
                return input.value;
            })
            .filter(Boolean);

        if (values.length) {
            filters[key] = values;
        }
    });

    return filters;
}

function syncFiltersToUrl(form) {
    const url = new URL(window.location.href);
    const filters = getFormFilters(form);

    FILTER_KEYS.forEach(function (key) {
        url.searchParams.delete(key);
    });

    Object.keys(filters).forEach(function (key) {
        url.searchParams.set(key, filters[key].join(','));
    });

    const next = url.pathname + url.search + url.hash;
    const current = window.location.pathname + window.location.search + window.location.hash;

    if (next !== current) {
        window.history.replaceState({}, '', next);
    }
}

async function fetchFilteredProducts(form) {
    const data = new FormData(form);
    data.append('action', AJAX_ACTION);
    data.append('nonce', window.WPURLS.nonce);

    const response = await fetch(window.WPURLS.ajaxUrl, {
        method: 'POST',
        credentials: 'same-origin',
        body: data
    });

    const payload = await response.json();

    return payload && payload.success ? payload.data.html : null;
}

function restoreMosaic(results, mosaicTemplate) {
    if (mosaicTemplate) {
        results.innerHTML = mosaicTemplate.innerHTML;
        return;
    }

    results.innerHTML = '';
}

function isMobileFilters() {
    return window.matchMedia(MOBILE_MQ).matches;
}

function setPanelOpenState(form, toggle, isOpen) {
    form.classList.toggle('product-filters--open', isOpen);
    toggle.setAttribute('aria-expanded', String(isOpen));
}

function setMobileOffcanvasChrome(locked) {
    document.body.classList.toggle('no-scroll', locked);
    document.body.classList.toggle(BODY_OFFCANVAS_CLASS, locked);
}

function getOffcanvasInstance(panel) {
    if (!window.bootstrap?.Offcanvas) {
        return null;
    }

    return window.bootstrap.Offcanvas.getOrCreateInstance(panel);
}

function disposeOffcanvas(panel) {
    const instance = window.bootstrap?.Offcanvas?.getInstance(panel);
    if (!instance) {
        return;
    }

    instance.dispose();
    panel.classList.remove('show', 'showing', 'hiding');
    panel.style.visibility = '';
    panel.removeAttribute('aria-modal');
    panel.removeAttribute('role');
}

function syncFilterDropdownMode(form) {
    if (!window.bootstrap?.Dropdown) {
        return;
    }

    const mobile = isMobileFilters();

    form.querySelectorAll('[data-product-filters-group] > [data-bs-toggle="dropdown"]').forEach(function (trigger) {
        const existing = window.bootstrap.Dropdown.getInstance(trigger);
        if (existing) {
            existing.dispose();
        }

        window.bootstrap.Dropdown.getOrCreateInstance(trigger, {
            autoClose: 'outside',
            display: mobile ? 'static' : 'dynamic',
        });
    });
}

function initFiltersToggle(form) {
    const toggle = form.querySelector(TOGGLE_SELECTOR);
    const panel = form.querySelector(PANEL_SELECTOR);
    const panelInner = panel?.querySelector('.product-filters-panel-inner');

    if (!toggle || !panel || !panelInner) {
        return;
    }

    let onPanelTransitionEnd = null;
    const mediaQuery = window.matchMedia(MOBILE_MQ);

    function openDesktopPanel() {
        setPanelOpenState(form, toggle, true);
        panelInner.style.overflow = 'hidden';

        if (onPanelTransitionEnd) {
            panel.removeEventListener('transitionend', onPanelTransitionEnd);
            onPanelTransitionEnd = null;
        }

        onPanelTransitionEnd = function (event) {
            if (event.target !== panel || event.propertyName !== 'grid-template-rows') {
                return;
            }
            if (form.classList.contains('product-filters--open')) {
                panelInner.style.overflow = 'visible';
            }
            panel.removeEventListener('transitionend', onPanelTransitionEnd);
            onPanelTransitionEnd = null;
        };
        panel.addEventListener('transitionend', onPanelTransitionEnd);
    }

    function closeDesktopPanel() {
        setPanelOpenState(form, toggle, false);
        panelInner.style.overflow = 'hidden';

        if (onPanelTransitionEnd) {
            panel.removeEventListener('transitionend', onPanelTransitionEnd);
            onPanelTransitionEnd = null;
        }
    }

    toggle.addEventListener('click', function () {
        if (isMobileFilters()) {
            getOffcanvasInstance(panel)?.toggle();
            return;
        }

        if (form.classList.contains('product-filters--open')) {
            closeDesktopPanel();
            return;
        }

        openDesktopPanel();
    });

    panel.addEventListener('show.bs.offcanvas', function () {
        setPanelOpenState(form, toggle, true);
        setMobileOffcanvasChrome(true);
    });

    panel.addEventListener('hidden.bs.offcanvas', function () {
        setPanelOpenState(form, toggle, false);
        setMobileOffcanvasChrome(false);
    });

    function onViewportChange(event) {
        if (event.matches) {
            closeDesktopPanel();
            syncFilterDropdownMode(form);
            return;
        }

        disposeOffcanvas(panel);
        document.querySelectorAll('body > .offcanvas-backdrop').forEach(function (backdrop) {
            backdrop.remove();
        });
        document.body.style.removeProperty('overflow');
        document.body.style.removeProperty('padding-right');
        setMobileOffcanvasChrome(false);
        closeDesktopPanel();
        syncFilterDropdownMode(form);
    }

    if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', onViewportChange);
    } else if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(onViewportChange);
    }
}

function initProductFilters() {
    const form = document.querySelector(FORM_SELECTOR);
    const results = document.querySelector(RESULTS_SELECTOR);
    const mosaicTemplate = document.querySelector(MOSAIC_SELECTOR);

    if (!form || !results || !window.WPURLS) {
        return;
    }

    async function updateResults() {
        syncFilterCounts(form);
        syncFiltersToUrl(form);

        if (!hasActiveFilters(form)) {
            restoreMosaic(results, mosaicTemplate);
            return;
        }

        results.setAttribute('aria-busy', 'true');

        try {
            const markup = await fetchFilteredProducts(form);
            if (markup !== null) {
                results.innerHTML = markup;
            }
        } finally {
            results.removeAttribute('aria-busy');
        }
    }

    form.addEventListener('change', updateResults);

    form.addEventListener('submit', function (event) {
        event.preventDefault();
        updateResults();
    });

    // type="reset" ripristina i default HTML (checkbox già checked da URL),
    // quindi smarchiamo esplicitamente tutte le opzioni.
    const resetBtn = form.querySelector(RESET_SELECTOR);
    if (resetBtn) {
        resetBtn.addEventListener('click', function () {
            form.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
                input.checked = false;
            });
            updateResults();
        });
    }

    syncFilterCounts(form);
    syncFilterDropdownMode(form);
    initFiltersToggle(form);
}

if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initProductFilters, { once: true });
} else {
    initProductFilters();
}
